import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { Order } from "@/hooks/useOrders"; // Using the Order type from useOrders which is now AppOrder
import { Badge } from "@/components/ui/badge";

interface ViewOfferDialogProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
}

export const ViewOfferDialog = ({ isOpen, onClose, order }: ViewOfferDialogProps) => {
  if (!order || !order.offer_id) {
    return null;
  }

  let claimStatusString: string;
  if (order.offer_items_claimed && typeof order.offer_items_claimed === 'object') {
    // If it's an object (e.g., {"items": [...]}), it's claimed.
    claimStatusString = "Claimed";
  } else if (typeof order.offer_items_claimed === 'string' && order.offer_items_claimed.toLowerCase() === 'claimed') {
    // If it's already the string "Claimed"
    claimStatusString = "Claimed";
  }
  else {
    // Otherwise, consider it Pending (handles null, undefined, or other string values)
    claimStatusString = "Pending";
  }

  const claimLink = `https://mmpoffers.netlify.app/offer?order_id=${order.order_id}&offer_id=${order.offer_id}`;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Offer Details</DialogTitle>
          <DialogDescription>
            Review the offer details for order #{order.order_no || order.order_id}.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-[110px_1fr] items-center gap-4">
            <span className="text-sm font-medium text-muted-foreground">Offer name</span>
            <span className="text-sm font-medium text-foreground">{order.offer_name || "N/A"}</span>
          </div>
          <div className="grid grid-cols-[110px_1fr] items-start gap-4">
            <span className="text-sm font-medium text-muted-foreground">SMS text</span>
            <p className="text-sm whitespace-pre-wrap text-foreground">{order.offer_text_sms || "N/A"}</p>
          </div>
          <div className="grid grid-cols-[110px_1fr] items-center gap-4">
            <span className="text-sm font-medium text-muted-foreground">Offer value</span>
            <span className="text-sm font-semibold text-foreground tabular-nums">
              {order.offer_value !== null && order.offer_value !== undefined
                ? `$${Number(order.offer_value).toFixed(2)}`
                : "N/A"}
            </span>
          </div>
          <div className="grid grid-cols-[110px_1fr] items-center gap-4">
            <span className="text-sm font-medium text-muted-foreground">Claim status</span>
            <Badge
              variant="outline"
              className={
                claimStatusString === "Claimed"
                  ? "border-transparent bg-status-success/15 text-status-success"
                  : "border-transparent bg-muted text-muted-foreground"
              }
            >
              {claimStatusString}
            </Badge>
          </div>
          <div className="grid grid-cols-[110px_1fr] items-center gap-4">
            <span className="text-sm font-medium text-muted-foreground">Claim link</span>
            <a
              href={claimLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-status-info hover:underline break-all"
            >
              {claimLink}
            </a>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
